import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import {
  DocumentData,
  DocumentReference,
  Firestore,
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  onSnapshot,
  orderBy,
  Timestamp,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import {
  StorageReference,
  UploadTask,
  UploadTaskSnapshot,
  deleteObject,
  getDownloadURL,
  getStorage,
  ref,
  uploadBytes,
  uploadBytesResumable,
} from "firebase/storage";
import { BehaviorSubject, from, Observable, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { environment } from "src/environments/environment";

@Injectable({
  providedIn: "root",
})
export class PlaysService {
  private playData = new BehaviorSubject<any>(null);
  streams: any;
  discography: any[];
  firestore: Firestore = getFirestore();
  constructor(private http: HttpClient) {}

  generateId(type: string, albumId?: string): string {
    // This creates a reference to a new document in a dummy collection '_'
    // and returns its unique ID without actually writing to the database.
    let docRef;

    if (type === "album")
      docRef = doc(collection(this.firestore, "discography"));
    if (type === "video") docRef = doc(collection(this.firestore, "videos"));
    if (type === "track")
      docRef = doc(collection(this.firestore, `discography/${albumId}/tracks`));
    return docRef.id;
  }

  /**
   * Adds a new track document to the 'tracks' subcollection of a specific album.
   * @param albumId The ID of the parent album.
   * @param trackId The unique ID for the new track.
   * @param trackData The data for the new track.
   */
  addTrackToAlbum(
    albumId: string,
    trackId: string,
    trackData: any
  ): Promise<void> {
    const trackRef = doc(
      this.firestore,
      `discography/${albumId}/tracks/${trackId}`
    );
    // Using setDoc because we are providing our own ID (trackId).
    return setDoc(trackRef, trackData);
  }

  /**
   * Updates an existing track document in the 'tracks' subcollection.
   * @param albumId The ID of the parent album.
   * @param trackId The ID of the track to update.
   * @param data The fields to update.
   */
  updateTrackInAlbum(
    albumId: string,
    trackId: string,
    data: any
  ): Promise<void> {
    const trackRef = doc(
      this.firestore,
      `discography/${albumId}/tracks/${trackId}`
    );
    // Using updateDoc to merge data into an existing document.
    return updateDoc(trackRef, data);
  }

  /**
   * Updates the trackIndex for multiple tracks in an album using a batch write.
   * @param albumId The ID of the parent album.
   * @param tracks The array of tracks with their new order.
   */
  updateTrackOrder(albumId: string, tracks: any[]): Promise<void> {
    const batch = writeBatch(this.firestore);

    tracks.forEach((track, index) => {
      const trackRef = doc(
        this.firestore,
        `discography/${albumId}/tracks/${track.id}`
      );
      batch.update(trackRef, { trackIndex: index });
    });

    return batch.commit();
  }
  // Other methods needed by upload.component.ts

  addDisco(albumData: any, albumId: string): Promise<void> {
    const albumRef = doc(this.firestore, "discography", albumId);
    return setDoc(albumRef, {
      ...albumData,
      created: new Date().toISOString(),
    });
  }

  updateAlbum(albumId: string, data: any): Promise<void> {
    const albumRef = doc(this.firestore, "discography", albumId);
    return updateDoc(albumRef, data);
  }

  getAlbumById(albumId: string): Observable<any> {
    const albumRef = doc(this.firestore, "discography", albumId);
    return new Observable((observer) => {
      const unsubscribe = onSnapshot(
        albumRef,
        (docSnap) => {
          if (docSnap.exists()) {
            observer.next({ id: docSnap.id, ...docSnap.data() });
          } else {
            observer.next(null); // Or handle as an error
          }
        },
        (error) => {
          observer.error(error);
        }
      );
      // Teardown logic to unsubscribe when the Observable is unsubscribed
      return { unsubscribe };
    });
  }

  getTracksForAlbum(albumId: string): Observable<any[]> {
    const tracksCollection = collection(
      this.firestore,
      `discography/${albumId}/tracks`
    );
    return new Observable((observer) => {
      const unsubscribe = onSnapshot(
        tracksCollection,
        (querySnapshot) => {
          const tracks = querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          observer.next(tracks);
        },
        (error) => {
          observer.error(error);
        }
      );
      // Teardown logic
      return { unsubscribe };
    });
  }

  addVideo(videoData: any, videoId: string): Promise<void> {
    const videoRef = doc(this.firestore, "videos", videoId);
    return setDoc(videoRef, videoData);
  }

  async getDiscographyByCreator(creatorId: string): Promise<any[]> {
    const discographyRef = collection(this.firestore, "discography");
    // This query is much more efficient as it only fetches the top-level album documents
    // for the user, without fetching all the tracks for each album.
    const q = query(discographyRef, where("artistId", "==", creatorId));
    try {
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error("Error getting discography by creator:", error);
      throw error;
    }
  }

  updateVideo(videoId: string, data: any): Promise<void> {
    const videoRef = doc(this.firestore, "videos", videoId);
    return updateDoc(videoRef, data);
  }

  async getVideosByCreator(creatorId: string): Promise<any[]> {
    const videosRef = collection(this.firestore, "videos");
    const q = query(videosRef, where("creator.id", "==", creatorId));
    try {
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error("Error getting videos by creator:", error);
      throw error;
    }
  }

  async searchCreators(queryText: string): Promise<any[]> {
    const usersRef = collection(this.firestore, "users");
    // Firestore is case-sensitive. A common pattern is to store a lowercase version of the field.
    const endText = queryText.toLowerCase() + "\uf8ff";
    const q = query(
      usersRef,
      where("creator", "==", true),
      where("displayName_lowercase", ">=", queryText.toLowerCase()),
      where("displayName_lowercase", "<=", endText)
    );
    try {
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error("Error searching creators:", error);
      throw error;
    }
  }

  async createStubUser(name: string): Promise<{ id: string; name: string }> {
    const newUserRef = doc(collection(this.firestore, "users"));
    const newUser = {
      displayName: name,
      displayName_lowercase: name.toLowerCase(),
      creator: false, // This user is not a creator yet
      claimed: false, // This account can be claimed later

      created: new Date().toISOString(),
    };
    await setDoc(newUserRef, newUser);
    return { id: newUserRef.id, name: name };
  }

  setPlayData(data: any) {
    this.playData.next(data);
  }

  getPlayData(): Observable<any> {
    return this.playData.asObservable();
  }

  async getAllDiscography() {
    const userItem = localStorage.getItem("user");
    if (!userItem) {
      console.warn("getAllDiscography called without a logged-in user.");
      return []; // Prevent crash if no user is in local storage
    }
    const creatorId = JSON.parse(userItem).id;
    const discographyRef = collection(getFirestore(), "discography");
    const q = query(
      discographyRef,
      orderBy("released", "desc"),
      where("artistId", "==", creatorId)
    );

    try {
      const querySnapshot = await getDocs(q);
      const albumPromises = querySnapshot.docs.map(async (docSnapshot) => {
        const docData = docSnapshot.data();
        const tracksQuery = query(
          collection(docSnapshot.ref, "tracks"),
          orderBy("trackIndex", "asc")
        );
        const tracksSnapshot = await getDocs(tracksQuery);

        const tracks = tracksSnapshot.docs.map((trackDoc) => ({
          id: trackDoc.id,
          ...trackDoc.data(),
        }));

        return {
          id: docSnapshot.id,
          ...docData,
          tracks,
        };
      });

      const discographyList = await Promise.all(albumPromises);
      this.discography = discographyList;
      console.log(this.discography);
      return discographyList; // This list now includes tracks for each album
    } catch (error) {
      console.error("Error getting documents: ", error);
      throw error; // or handle it as needed
    }
  }

  // Add this method inside your PlaysService class
  uploadProfileImage(
    file: File,
    type: "avatar" | "banner",
    creatorId: string
  ): Observable<{ url: string }> {
    const formData = new FormData();

    // The backend expects these specific keys in the form data
    formData.append("file", file, file.name);
    formData.append("type", type);
    formData.append("creatorId", creatorId);

    return this.http.post<{ url: string }>(
      `${environment.myBackend}/upload-profile-image`,
      formData
    );
  }

  public async getSubscriptionPaymentsByCreator(
    creatorId: string,
    startDate?: Date
  ): Promise<any[]> {
    // This is an example using Firestore. Adjust it to your backend implementation.
    // It fetches all payment documents for a specific creator.
    // NOTE: This assumes each document in 'subPayments' has a 'creatorId', 'userId', and 'amount'.
    try {
      const paymentsCollectionRef = collection(
        this.firestore,
        `users/${creatorId}/subPayments`
      );
      let q = query(paymentsCollectionRef);
      if (startDate) {
        // The 'date' field is an ISO string, so we must convert the startDate to an ISO string for the query.
        const startDateISO = startDate.toISOString();
        q = query(q, where("date", ">=", startDateISO));
      }
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error("Error fetching subscription payments:", error);
      return []; // Return an empty array on error to prevent crashes
    }
  }

  public async getSalesByCreator(
    creatorId: string,
    startDate?: Date
  ): Promise<any[]> {
    try {
      const salesCollectionRef = collection(
        this.firestore,
        `users/${creatorId}/sales`
      );
      let q = query(salesCollectionRef);
      if (startDate) {
        q = query(q, where("date", ">=", startDate));
      }
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error("Error fetching sales data:", error);
      return [];
    }
  }

  async requestCreatorPayout(
    creatorId: string,
    payoutAmount: number,
    transactionsToPay: any[]
  ): Promise<void> {
    const batch = writeBatch(this.firestore);

    // 1. Create a new document in the `pendingPayments` collection for admin approval.
    const pendingPaymentRef = doc(
      collection(this.firestore, "pendingPayments")
    );
    const transactionIds = transactionsToPay.map((t) => t.id);

    batch.set(pendingPaymentRef, {
      creatorId,
      amount: payoutAmount,
      requestedDate: serverTimestamp(),
      status: "pending_approval", // Status for the overall payout request
      transactionIds,
    });

    // 2. Update the status of each original transaction to 'pending' to prevent re-submission.
    transactionsToPay.forEach((transaction) => {
      const collectionName =
        transaction.originalType === "sale" ? "sales" : "subPayments";
      const docRef = doc(
        this.firestore,
        `users/${creatorId}/${collectionName}`,
        transaction.id
      );
      batch.update(docRef, { status: "pending" });
    });

    // 3. Commit all writes atomically.
    return batch.commit();
  }

  async getCreatorApplications(): Promise<any[]> {
    const appsRef = collection(this.firestore, "creatorApplications");
    const q = query(appsRef);
    try {
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error("Error getting creator applications:", error);
      throw error;
    }
  }

  async getPageViewsByCreator(
    creatorId: string,
    sinceDate: Date
  ): Promise<any[]> {
    try {
      const pageViewsCol = collection(this.firestore, "pageViews");
      const q = query(
        pageViewsCol,
        where("creatorId", "==", creatorId),
        where("timestamp", ">=", Timestamp.fromDate(sinceDate))
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((doc) => doc.data());
    } catch (error) {
      console.error("Error fetching page views:", error);
      return [];
    }
  }

  async approveCreatorApplication(application: any): Promise<void> {
    const batch = writeBatch(this.firestore);

    // 1. Update the user document
    const userRef = doc(this.firestore, "users", application.userId);
    batch.update(userRef, {
      creatorType: "creator",
      socialLinks: application.socialLinks,
      creatorApplicationStatus: "approved",
    });

    // 2. Delete the application document
    const appRef = doc(this.firestore, "creatorApplications", application.id);
    batch.delete(appRef);

    return batch.commit();
  }

  async denyCreatorApplication(applicationId: string): Promise<void> {
    const appRef = doc(this.firestore, "creatorApplications", applicationId);
    return deleteDoc(appRef);
  }

  async getPendingPayments(): Promise<any[]> {
    const paymentsRef = collection(this.firestore, "pendingPayments");
    const q = query(paymentsRef, where("status", "==", "pending_approval"));
    try {
      const querySnapshot = await getDocs(q);
      // Fetch creator display name for each payment for a better UI
      const paymentPromises = querySnapshot.docs.map(async (docSnapshot) => {
        const payment: any = { id: docSnapshot.id, ...docSnapshot.data() };
        const userDoc = await getDoc(
          doc(this.firestore, "users", payment.creatorId)
        );
        if (userDoc.exists()) {
          payment.creatorName = userDoc.data().displayName;
        }
        return payment;
      });
      return Promise.all(paymentPromises);
    } catch (error) {
      console.error("Error getting pending payments:", error);
      throw error;
    }
  }

  async processPendingPayment(
    paymentId: string,
    newStatus: "approved" | "denied"
  ): Promise<void> {
    const batch = writeBatch(this.firestore);
    const paymentRef = doc(this.firestore, "pendingPayments", paymentId);

    const paymentDoc = await getDoc(paymentRef);
    if (!paymentDoc.exists()) {
      throw new Error("Payment document not found!");
    }
    const paymentData = paymentDoc.data();

    // 1. Update the main pendingPayment document
    batch.update(paymentRef, { status: newStatus });

    // 2. Determine the new status for individual transactions
    const newTransactionStatus = newStatus === "approved" ? "paid" : "unpaid";

    // 3. Update each individual transaction by checking both possible collections
    for (const transactionId of paymentData.transactionIds) {
      const creatorId = paymentData.creatorId;
      const saleRef = doc(
        this.firestore,
        `users/${creatorId}/sales`,
        transactionId
      );
      const subRef = doc(
        this.firestore,
        `users/${creatorId}/subPayments`,
        transactionId
      );

      const saleDoc = await getDoc(saleRef);
      if (saleDoc.exists()) {
        batch.update(saleRef, { status: newTransactionStatus });
      } else {
        batch.update(subRef, { status: newTransactionStatus });
      }
    }

    return batch.commit();
  }

  public async getConcertsByCreator(creatorId: string): Promise<any[]> {
    const concertsRef = collection(this.firestore, "concerts");
    const q = query(
      concertsRef,
      where("artistId", "==", creatorId),
      orderBy("date", "desc")
    );
    try {
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error("Error getting concerts by creator:", error);
      throw error;
    }
  }

  public addConcert(concertData: any): Promise<DocumentReference> {
    const concertsRef = collection(this.firestore, "concerts");
    return addDoc(concertsRef, concertData);
  }

  public updateConcert(concertId: string, data: any): Promise<void> {
    const concertRef = doc(this.firestore, "concerts", concertId);
    return updateDoc(concertRef, data);
  }

  public deleteConcert(concertId: string): Promise<void> {
    const concertRef = doc(this.firestore, "concerts", concertId);
    return deleteDoc(concertRef);
  }

  public updateUserMemberships(
    userId: string,
    memberships: any[]
  ): Promise<void> {
    const userRef = doc(this.firestore, "users", userId);
    return updateDoc(userRef, { memberships });
  }

  public async isHandleUnique(handle: string): Promise<boolean> {
    const q = query(
      collection(this.firestore, "users"),
      where("handle", "==", handle)
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.empty;
  }

  public updateUserProfile(userId: string, data: any): Promise<void> {
    const userRef = doc(this.firestore, "users", userId);
    return updateDoc(userRef, data);
  }

  async deleteAlbum(id: string): Promise<void> {
    const albumDocRef = doc(this.firestore, `discography/${id}`);
    return deleteDoc(albumDocRef);
  }

  async deleteVideo(id: string): Promise<void> {
    const videoDocRef = doc(this.firestore, `videos/${id}`);
    return deleteDoc(videoDocRef);
  }

  async getArt(title: string) {
    // ... inside an async function or method
    try {
      const ref = doc(this.firestore, "discography", title);
      const docSnapshot = await getDoc(ref);

      if (docSnapshot.exists()) {
        const albumData = docSnapshot.data();
        const art = albumData.art; // Here is your 'art' field

        return art;
        // Do something with the art URL...
      } else {
        console.log("No such document!");
        return null;
      }
    } catch (error) {
      console.error("Error fetching document: ", error);
    }
  }

  // uploadStorage(
  //   refPath: string,
  //   file: Blob
  // ): Observable<{ progress: number; link: string }> {
  //   const storageRef = ref(this.storage, refPath);
  //   const uploadTask = uploadBytesResumable(storageRef, file);

  //   // Create an observable to track upload progress and return download URL
  //   return new Observable<{ progress: number; link: string }>((observer) => {
  //     uploadTask.on(
  //       "state_changed",
  //       (snapshot: UploadTaskSnapshot) => {
  //         // Calculate progress percentage
  //         const progress =
  //           (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
  //         observer.next({ progress, link: "" }); // Emit progress, URL will be updated later
  //       },
  //       (error) => {
  //         observer.error(error); // Handle error
  //       },
  //       () => {
  //         // Upload completed successfully, get the download URL
  //         getDownloadURL(uploadTask.snapshot.ref).then((link) => {
  //           observer.next({ progress: 100, link: link }); // Update progress with 100% and URL
  //           observer.complete(); // Complete the observable
  //         });
  //       }
  //     );
  //   });
  // }

  // async copyFileAndGetURL(ref1: string, ref2: string): Promise<string> {
  //   const tempRef = ref(this.storage, ref1);
  //   const finalRef = ref(this.storage, ref2);
  //   const tempURL = await getDownloadURL(tempRef);
  //   const response = await fetch(tempURL);
  //   const blob = await response.blob();

  //   await uploadBytes(finalRef, blob);
  //   const newURL = await getDownloadURL(finalRef);
  //   await deleteObject(tempRef);

  //   return newURL;
  // }
  async updateTrackField() {
    const streamsRef = collection(getFirestore(), "streams"); // Reference to the 'streams' collection
    const q = query(streamsRef, where("track", "==", "beThat/0")); // Query for documents with track = "bang'n/0"

    try {
      const querySnapshot = await getDocs(q);
      const batch = writeBatch(getFirestore()); // Create a Firestore batch

      querySnapshot.forEach((docSnapshot) => {
        const docRef = doc(getFirestore(), "streams", docSnapshot.id);

        // Update the track field to "ipcYfzd8YptXQ6NUUyoW"
        batch.update(docRef, { track: "OcYNNY5v9QXqCE1A6mTK" });
      });

      // Commit the batch
      await batch.commit();
      console.log("All track fields have been updated.");
    } catch (error) {
      console.error("Error updating track fields:", error);
    }
  }
  async getStats(startDate?: Date) {
    let data = [];
    const userItem = localStorage.getItem("user");
    if (!userItem) {
      console.warn("getStats called without a logged-in user.");
      return []; // Prevent crash if no user is in local storage
    }
    const artistId = JSON.parse(userItem).id;
    try {
      // Query the 'streams' collection where 'artistId' array contains the incoming artistId
      const streamsRef = collection(getFirestore(), "streams");
      let q = query(streamsRef, where("artistId", "array-contains", artistId));
      if (startDate) {
        q = query(q, where("date", ">=", startDate));
      }
      const querySnapshot = await getDocs(q);
      const data = querySnapshot.docs.map((doc) => doc.data());

      this.streams = data;
      const trac = data.filter((item) => item.user === "guest");
      console.log(trac);

      return data;
    } catch (error) {
      console.error("Error fetching stats:", error);
      throw error; // Re-throw the error for further handling
    }
  }

  async getVideoViews(startDate?: Date) {
    let data = [];
    const userItem = localStorage.getItem("user");
    if (!userItem) {
      console.warn("getVideoViews called without a logged-in user.");
      return []; // Prevent crash if no user is in local storage
    }
    const creatorId = JSON.parse(userItem).id;
    try {
      // This assumes a 'views' collection exists similar to 'streams'
      const viewsRef = collection(getFirestore(), "views");
      let q = query(viewsRef, where("creatorId", "==", creatorId));
      if (startDate) {
        q = query(q, where("date", ">=", startDate));
      }
      const querySnapshot = await getDocs(q);
      querySnapshot.forEach((doc) => {
        data.push(doc.data());
      });

      return data;
    } catch (error) {
      console.error("Error fetching video views:", error);
      throw error;
    }
  }

  returnStreams() {
    return this.streams;
  }
}
